import { Button, Table, Thead, Tbody, Tr, Th, Td, chakra, Flex, Text, Avatar } from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { useTable, useSortBy, Column } from 'react-table'
import { FiChevronDown, FiChevronUp, FiTrash } from 'react-icons/fi'
import { Resource } from '../types'
import { useMutation, useQuery } from '../convex/_generated/react'
import { Id } from '../convex/_generated/dataModel'
import ResourceFormModal from '../components/Modals/ResourceFormModal'
import EmptyState from '../components/EmptyState'

const Resources = () => {
  const [showModal, setShowModal] = useState(false)
  const [editResourceId, setEditResourceId] = useState<Id>()
  const deleteDocument = useMutation('deleteDocument')

  const list = useQuery('listResources')

  const data = useMemo<Array<Resource>>(
    () => list || [], [list]
  )

  const loading = typeof list === 'undefined'
  const emptyList = !list || list.length <= 0

  const columns = useMemo<Column<Resource>[]>(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({ value }: any) => {
          return (
            <Flex gap={5} align={'center'}>
              <Avatar name={value} />
              <Text>{ value }</Text>
            </Flex>
          )
        }
      },
      {
        Header: 'Designation',
        accessor: 'designation'
      },
      {
        Header: 'Email',
        accessor: 'email'
      },
      {
        Header: 'Location',
        accessor: 'location'
      },
      {
        Header: () => <div style={{ textAlign: 'right' }}>Actions</div>,
        accessor: '_id',
        Cell: ({ value }: any) => {
          return (
            <div style={{ textAlign: 'right' }}>
              <Button onClick={(e) => {
                e.stopPropagation()
                list && deleteDocument(value)
              }}><FiTrash /></Button>
            </div>
          )
        }
      }
    ],
    []
  )

  const handleRow = (row: Resource) => {
    setEditResourceId(row._id)
    setShowModal(true)
  }

  const handleNewResource = () => {
    setEditResourceId(undefined)
    setShowModal(true)
  }

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data }, useSortBy)

  return (
    <div>
      <Flex alignItems="center" justifyContent="space-between">
        <Text fontSize='3xl'>Resources</Text>
        <Button onClick={handleNewResource}>New Resource</Button>
      </Flex>
      { emptyList
        ? <EmptyState
            title={ loading ? 'Loading...' : 'No resources' }
            ctaText={ loading ? undefined : 'New Resource' }
            onClick={handleNewResource}
          />
        : null
      }
      <Table style={{ visibility: (loading || emptyList) ? 'hidden' : 'visible' }} {...getTableProps()}>
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column: any, index) => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  isNumeric={column.isNumeric}
                  key={index}
                >
                  {column.render('Header')}
                  <chakra.span pl='4'>
                    {column.isSorted
                      ? (
                          column.isSortedDesc
                            ? (
                        <FiChevronDown aria-label='sorted descending' />
                              )
                            : (
                        <FiChevronUp aria-label='sorted ascending' />
                              )
                        )
                      : null}
                  </chakra.span>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {rows.map((row, index) => {
            prepareRow(row)
            return (
              <Tr {...row.getRowProps()} onClick={() => handleRow(row.original)} key={index}>
                {row.cells.map((cell: any, index) => (
                  <Td {...cell.getCellProps()} isNumeric={cell.column.isNumeric} key={index}>
                    {cell.render('Cell')}
                  </Td>
                ))}
              </Tr>
            )
          })}
        </Tbody>
      </Table>
      <ResourceFormModal
        _id={editResourceId}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </div>
  )
}

export default Resources
